import { TileConfig, UnifiedIntegrationId } from './types';
import {
  Category,
  Group,
  Integration,
  PRICING_MODULES,
} from 'integrations/types';
import { Features } from '@ardoq/features';
import {
  ardoqUrl,
  azureUrl,
  celonisUrl,
  jiraUrl,
  signavioUrl,
  symbioUrl,
  sharepointUrl,
  microsoftEntraIdUrl,
  importBuilderUrl,
} from '@ardoq/integrations/logos';
import { UNIFIED_INTEGRATION_IDS } from './constants';
import { initialStates } from './initialState';
import { OverviewRoute } from 'integrations/common/navigation/types';
import { OrgAccessLevel } from '@ardoq/api-types';

const tileConfigs: Record<UnifiedIntegrationId, TileConfig> = {
  'jira-software': {
    categories: [Category.SERVICE, Category.IMPORTER],
    description: 'Import from JIRA Software',
    logoUrl: jiraUrl,
    disabledFeatures: [Features.QUICK_START_RESTRICT_AVAILABLE_INTEGRATIONS],
    group: Group.INTEGRATIONS,
    requiredPricingModule: PRICING_MODULES.TRANSFORMATION_MODULE,
  },
  'entra-id': {
    categories: [Category.SERVICE, Category.IMPORTER],
    description: 'Import from Microsoft Entra ID',
    logoUrl: microsoftEntraIdUrl,
    disabledFeatures: [Features.QUICK_START_RESTRICT_AVAILABLE_INTEGRATIONS],
    group: Group.INTEGRATIONS,
  },
  signavio: {
    categories: [Category.SERVICE, Category.IMPORTER],
    description: 'Import from SAP Signavio',
    logoUrl: signavioUrl,
    disabledFeatures: [Features.QUICK_START_RESTRICT_AVAILABLE_INTEGRATIONS],
    group: Group.INTEGRATIONS,
  },
  ardoq: {
    categories: [Category.SERVICE, Category.IMPORTER],
    description: 'Import from Ardoq',
    logoUrl: ardoqUrl,
    requiredFeatures: [Features.ARDOQ_IMPORT_ENABLE],
    isBeta: true,
    group: Group.INTEGRATIONS,
  },
  'import-builder': {
    categories: [Category.SERVICE, Category.IMPORTER],
    description: 'Build custom API integrations with our visual builder',
    isNew: true,
    logoUrl: importBuilderUrl,
    group: Group.INTEGRATIONS,
  },
  'ardoq-reference-data': {
    categories: [Category.SERVICE, Category.IMPORTER],
    description: 'Import reference data from Ardoq',
    logoUrl: ardoqUrl,
    requiredFeatures: [Features.ARDOQ_REFERENCE_DATA_IMPORT_ENABLE],
    isBeta: true,
    group: Group.INTEGRATIONS,
  },
  'ardoq-sample-data': {
    categories: [Category.SERVICE, Category.IMPORTER],
    description: 'Import sample data from Ardoq',
    logoUrl: ardoqUrl,
    requiredFeatures: [Features.ARDOQ_SAMPLE_DATA_IMPORT_ENABLE],
    isBeta: true,
    group: Group.INTEGRATIONS,
  },
  'sharepoint-files': {
    categories: [Category.SERVICE, Category.IMPORTER],
    description: 'Import data from files stored in SharePoint',
    logoUrl: sharepointUrl,
    group: Group.INTEGRATIONS,
  },
  celonis: {
    categories: [Category.SERVICE, Category.IMPORTER],
    description: 'Import from Celonis',
    logoUrl: celonisUrl,
    requiredFeatures: [Features.CELONIS_ENABLE],
    disabledFeatures: [Features.QUICK_START_RESTRICT_AVAILABLE_INTEGRATIONS],
    isBeta: true,
    group: Group.INTEGRATIONS,
  },
  symbio: {
    categories: [Category.SERVICE, Category.IMPORTER],
    description: 'Import from Symbio',
    logoUrl: symbioUrl,
    disabledFeatures: [Features.QUICK_START_RESTRICT_AVAILABLE_INTEGRATIONS],
    group: Group.INTEGRATIONS,
  },
  'azure-resources': {
    categories: [Category.SERVICE, Category.IMPORTER],
    description: 'Import from Azure Resources',
    logoUrl: azureUrl,
    disabledFeatures: [Features.QUICK_START_RESTRICT_AVAILABLE_INTEGRATIONS],
    isNew: true,
    group: Group.INTEGRATIONS,
  },
} as const;

export const unifiedIntegrations: Integration[] = UNIFIED_INTEGRATION_IDS.map(
  id => {
    return {
      id,
      importerPath: id,
      name: initialStates[id].termsDictionary.name,
      typescriptImporter: {
        config: {
          allowedOverviewRoutes: [
            OverviewRoute.SCHEDULES,
            OverviewRoute.CONFIGURATIONS,
            OverviewRoute.CONNECTIONS,
          ],
        },
      },
      requiredOrgAccessLevel: OrgAccessLevel.ADMIN,
      // tileConfigs override any common config
      ...tileConfigs[id],
    };
  }
);
